import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import css from './FooterContainer.module.css';
import { Icons, LinkedLogo, NamedLink } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { clearSubscribeOutcome, subscribeToNewsletter } from '../../ducks/newsletter.duck';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

// Helper function to parse markdown links from footer blocks
const parseMarkdownLinks = markdown => {
  if (!markdown) return { links: [] };

  const lines = markdown.split('\n').filter(line => line.trim());
  const links = [];

  for (let i = 0; i < lines.length; i++) {
    // Remove list marker if present (- )
    const cleanLine = lines[i].replace(/^-\s*/, '');
    const match = cleanLine.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (match) {
      links.push({ label: match[1], href: match[2] });
    }
  }

  return { links };
};

// Helper function to get the correct NamedLink name from a path.
// Only returns mappings for routes that actually exist in routeConfiguration —
// if you add a Footer link for a path that doesn't have a registered route,
// it will be rendered as a plain <a href> fallback below instead of crashing.
const getNamedLinkFromPath = path => {
  const pathMapping = {
    '/profile-settings': 'ProfileSettingsPage',
    '/account': 'ProfileSettingsPage',
    '/favorites': 'FavoritesPage',
    '/wishlist': 'FavoritesPage',
    '/cart': 'CartPage',
    '/privacy-policy': 'PrivacyPolicyPage',
    '/terms-of-service': 'TermsOfServicePage',
    '/terms': 'TermsOfServicePage',
    '/sell': 'SellPage',
    '/purchase-protection': 'PurchaseProtectionPage',
    '/login': 'LoginPage',
    '/': 'LandingPage', // For empty paths
  };

  return pathMapping[path] || null;
};

// Check if we have a social media icon in our Icons component
const hasSocialMediaIcon = platform => {
  const supportedIcons = ['facebook', 'instagram', 'twitter', 'linkedin', 'youtube', 'pinterest'];
  return supportedIcons.includes(platform?.toLowerCase());
};

// Shown when the Console's footer asset is missing: only pages the app has
const fallbackColumns = [
  {
    titleId: 'FooterContainer.fallbackShopTitle',
    links: [
      { labelId: 'FooterContainer.fallbackAccount', href: '/account' },
      { labelId: 'FooterContainer.fallbackLogin', href: '/login' },
      { labelId: 'FooterContainer.fallbackWishlist', href: '/wishlist' },
      { labelId: 'FooterContainer.fallbackCart', href: '/cart' },
    ],
  },
  {
    titleId: 'FooterContainer.fallbackInformationTitle',
    links: [
      { labelId: 'FooterContainer.fallbackPurchaseProtection', href: '/purchase-protection' },
      { labelId: 'FooterContainer.fallbackSell', href: '/sell' },
    ],
  },
  {
    titleId: 'FooterContainer.fallbackCompanyTitle',
    links: [
      { labelId: 'FooterContainer.fallbackPrivacyPolicy', href: '/privacy-policy' },
      { labelId: 'FooterContainer.fallbackTerms', href: '/terms-of-service' },
    ],
  },
];

function FooterContainer() {
  const { footer = {}, marketplaceName } = useConfiguration();

  // Newsletter subscription state
  const intl = useIntl();
  const isAuthenticated = useSelector(state => !!state.auth?.isAuthenticated);
  const [email, setEmail] = useState('');
  // The signup itself is the newsletter duck's; only the "enter an email" check is here
  const dispatch = useDispatch();
  const { subscribeInProgress: isLoading, subscribeResult, subscribeError } = useSelector(
    state => state.newsletter
  );
  const [emailMissing, setEmailMissing] = useState(false);

  const handleSubscribe = e => {
    e.preventDefault();
    if (!email || email.trim() === '') {
      setEmailMissing(true);
      return;
    }
    setEmailMissing(false);
    dispatch(subscribeToNewsletter(email.trim())).then(subscribed => {
      if (subscribed) setEmail('');
    });
  };

  const successMessage = subscribeResult
    ? intl.formatMessage({
        id:
          subscribeResult === 'alreadySubscribed'
            ? 'FooterContainer.alreadySubscribed'
            : 'FooterContainer.subscribed',
      })
    : '';
  const errorMessage = emailMissing
    ? intl.formatMessage({ id: 'FooterContainer.emailRequired' })
    : subscribeError?.invalidEmail
    ? intl.formatMessage({ id: 'FooterContainer.emailInvalid' })
    : subscribeError
    ? intl.formatMessage({ id: 'FooterContainer.subscribeFailed' })
    : '';

  // Check if we have footer data
  const hasFooterData = Object.keys(footer).length > 0;

  // Parse footer blocks if available
  let footerColumns = [];
  if (hasFooterData && footer.blocks) {
    footerColumns = footer.blocks.map(block => {
      const parsed =
        block.text && block.text.content ? parseMarkdownLinks(block.text.content) : { links: [] };
      return {
        title: block.blockName || '',
        links: parsed.links,
      };
    });
  }

  // Get social media links
  const socialMediaLinks =
    hasFooterData && footer.socialMediaLinks ? footer.socialMediaLinks : null;

  // Get copyright text
  const copyrightText =
    hasFooterData && footer.copyright?.content
      ? footer.copyright.content
      : intl.formatMessage(
          { id: 'FooterContainer.copyright' },
          { year: new Date().getFullYear(), marketplaceName }
        );

  const translatedFallbackColumns = fallbackColumns.map(column => ({
    title: intl.formatMessage({ id: column.titleId }),
    links: column.links.map(link => ({
      label: intl.formatMessage({ id: link.labelId }, { marketplaceName }),
      href: link.href,
    })),
  }));

  // Use hosted columns or fallback
  // Signed in: no Login (or Sign up) link
  const hiddenWhenSignedIn = ['/login', '/signup'];
  const columnsToRender = (footerColumns.length > 0
    ? footerColumns
    : translatedFallbackColumns
  ).map(column => ({
    ...column,
    links: isAuthenticated
      ? column.links.filter(link => !hiddenWhenSignedIn.includes((link.href || '').split('?')[0]))
      : column.links,
  }));

  return (
    <div className={css.footer}>
      <div className={css.fixedWidthContainer}>
        <div className={css.topContent}>
          <div className={css.leftSec}>
            <LinkedLogo className={css.logoLink} logoClassName={css.logo} />
            <h2>
              <FormattedMessage id="FooterContainer.newsletterTitle" />
            </h2>
            <p>
              <FormattedMessage id="FooterContainer.newsletterText" />
            </p>
            <form className={css.newsLetterSec} onSubmit={handleSubscribe}>
              <input
                type="email"
                placeholder={intl.formatMessage({ id: 'FooterContainer.emailPlaceholder' })}
                aria-label={intl.formatMessage({ id: 'FooterContainer.emailLabel' })}
                value={email}
                onChange={e => {
                  setEmail(e.target.value);
                  setEmailMissing(false);
                  dispatch(clearSubscribeOutcome());
                }}
                disabled={isLoading}
              />
              <button className={css.subscribeBtn} type="submit" disabled={isLoading}>
                <FormattedMessage
                  id={isLoading ? 'FooterContainer.subscribing' : 'FooterContainer.subscribe'}
                />
              </button>
            </form>
            {successMessage && (
              <div className={css.successMessage} role="status">
                {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className={css.errorMessage} role="alert">
                {errorMessage}
              </div>
            )}
          </div>
          <div className={css.rightSec}>
            {columnsToRender.map((column, index) => (
              <div className={css.quickLinks} key={index}>
                {column.title && <h2>{column.title}</h2>}
                {column.links.map((link, linkIndex) => {
                  // Check if it's an external link (http/https)
                  if (
                    link.href &&
                    (link.href.startsWith('http://') || link.href.startsWith('https://'))
                  ) {
                    return (
                      <a key={linkIndex} href={link.href} target="_blank" rel="noopener noreferrer">
                        {link.label}
                      </a>
                    );
                  }
                  // Check if it's a mailto or tel link
                  if (
                    link.href &&
                    (link.href.startsWith('mailto:') || link.href.startsWith('tel:'))
                  ) {
                    return (
                      <a key={linkIndex} href={link.href}>
                        {link.label}
                      </a>
                    );
                  }
                  // Internal link — use NamedLink when the path maps to a
                  // registered route, otherwise fall back to a plain anchor.
                  // (Prevents NamedLink from throwing on unmapped paths like
                  // /contact, /returns, /about that exist in Footer config
                  // but aren't registered in routeConfiguration.)
                  const routeName = getNamedLinkFromPath(link.href);
                  return routeName ? (
                    <NamedLink key={linkIndex} name={routeName}>
                      {link.label}
                    </NamedLink>
                  ) : (
                    <a key={linkIndex} href={link.href}>
                      {link.label}
                    </a>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div className={css.bottomContent}>
          <div className={css.socialMediaLinks}>
            {/* The Console's social links: each to its own page, with a name for screen readers */}
            {(socialMediaLinks || []).map((social, index) => {
              const platform = social.link?.platform || '';
              const url = social.link?.url || ''; // Note: URL is inside link object
              const iconName = platform.toLowerCase();

              // Only platforms we have an icon for, linking out to a real page
              if (!hasSocialMediaIcon(iconName) || !/^https?:\/\//.test(url)) {
                return null;
              }

              return (
                <a
                  key={index}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={intl.formatMessage(
                    { id: 'FooterContainer.socialLinkLabel' },
                    { platform: iconName, marketplaceName }
                  )}
                >
                  <Icons name={iconName} />
                </a>
              );
            })}
          </div>
          <div className={css.copyRight}>{copyrightText}</div>
        </div>
      </div>
    </div>
  );
}

export default FooterContainer;
